import { v4 as uuidv4 } from 'uuid';
import {
  GDAIChatSource,
  NovaAvaChatPayload,
  NovaAvaCitation,
  NovaAvaEntities,
  NovaAvaStreamChunk,
  NovaAvaStreamImage,
  NovaRagChunk,
  NovaRagSearchResult,
} from '@/types';

// Nova's real chat backend: a raw NDJSON streaming endpoint on its own host
// (avadev-dsapi.globaldata.com), separate from the rest of the app's API which goes
// through middleware.ts's /api/* proxy. It's paired with a one-time-per-conversation
// call to mint a conv_id, made via pages/api/nova/create-conversation.ts (which calls
// the Stylus AI API server-side so its token never reaches the browser).

const AVA_STREAM_URL = '/api/nova/stream';

const RAG_SEARCH_URL = '/api/nova/rag/search';
const RAG_IMAGE_URL = '/api/nova/rag/image-url';
const RAG_REPORT_URL = '/api/nova/rag/reports/generate';
const RAG_REPORT_MODEL = process.env.NEXT_PUBLIC_NOVA_RAG_REPORT_MODEL;
const RAG_TOP_K = 20;

const NOVA_SITE_ID = '115';

const createRequestId = () => `rid-${uuidv4()}`;
export const createChatSessionKey = () => `csk-${uuidv4()}`;

const formatAvaDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day} / ${month} / ${date.getFullYear()}`;
};

// Trailing 12-month window anchored on the moment the user sends the query:
// endDate is today, startDate is exactly 12 months earlier (same day-of-month).
const buildNovaTimePeriod = (): NovaAvaEntities => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
  return {
    TimePeriod: {
      startDate: formatAvaDate(start),
      endDate: formatAvaDate(now),
    },
  };
};

// Called once per Nova conversation (see useNovaChat.ts) to get a conv_id from the
// Stylus AI createconversation API, which the AVA stream endpoint then keys history on.
export const createNovaConversationId = async (): Promise<string> => {
  const resp = await fetch('/api/nova/create-conversation', { method: 'POST' });

  if (!resp.ok) {
    throw new Error(`Failed to create Nova conversation (${resp.status})`);
  }

  const data: { succeeded: boolean; conversationId?: string } = await resp.json();
  if (!data.conversationId) {
    throw new Error('Failed to create Nova conversation (no conversation id returned)');
  }
  return data.conversationId;
};

export const buildNovaAvaPayload = (params: {
  query: string;
  name: string;
  userProfileId: string;
  chatSessionKey: string;
  convId: string;
  published12mFilter: boolean;
  filePaths?: string[];
}): NovaAvaChatPayload => ({
  user_message: params.query,
  name: params.name,
  userprofileid: params.userProfileId,
  requestid: createRequestId(),
  chatsessionkey: params.chatSessionKey,
  conv_id: params.convId,
  siteid: NOVA_SITE_ID,
  ...(params.published12mFilter ? { Entities: buildNovaTimePeriod() } : {}),
  ...(params.filePaths && params.filePaths.length > 0 ? { file_paths: params.filePaths } : {}),
});

const parseAvaStreamLine = (line: string): NovaAvaStreamChunk | null => {
  if (!line.trim()) return null;
  try {
    return JSON.parse(line);
  } catch {
    return null;
  }
};

export type NovaAvaStreamHandlers = {
  onToken?: (token: string) => void;
  onSources?: (sources: GDAIChatSource[]) => void;
  onSuggestedQuestions?: (questions: string[]) => void;
  onImages?: (images: NovaAvaStreamImage[]) => void;
  onCitations?: (citations: NovaAvaCitation[]) => void;
};

// Turns the stream's citations event into what the UI renders from:
//  - `sources`: every citation, numbered by its `citation_number` (the number the text's
//    [n] markers use), so an inline badge resolves to exactly the citation it names — an
//    image citation links to its article page. The reference chips and the export list
//    collapse repeats of one article separately (see dedupeSourcesByLink).
//  - `images`: each gallery image gets the citation number of the citation sharing its
//    `chunk_id`, since the images event's own number can be missing or pre-renumbering.
//    An image with no matching citation keeps the number it came with.
export const applyNovaCitations = (
  citations: NovaAvaCitation[],
  images: NovaAvaStreamImage[] = [],
): { sources: GDAIChatSource[]; images: NovaAvaStreamImage[] } => {
  const ordered = [...citations].sort((a, b) => a.citation_number - b.citation_number);

  const sources: GDAIChatSource[] = ordered.map((citation) => ({
    id: citation.citation_number,
    title: citation.title?.trim() ?? '',
    meta_Link: citation.url?.trim() ?? '',
    content: '',
    ...(citation.published_date ? { published_date: citation.published_date } : {}),
  }));

  const numberByChunk = new Map<string, number>();
  ordered.forEach((citation) => {
    [citation.chunk_id, ...(citation.chunk_ids ?? [])].forEach((chunkId) => {
      if (chunkId) numberByChunk.set(chunkId, citation.citation_number);
    });
  });
  const renumbered = images.map((image) => {
    const number = numberByChunk.get(image.chunk_id);
    return number === undefined ? image : { ...image, citation_number: number };
  });

  return { sources, images: renumbered };
};

const str = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const normaliseRagChunk = (result: NovaRagSearchResult): NovaRagChunk => {
  const meta = result.metadata ?? {};
  const modality = str(meta.modality) || 'text';
  const isImage = modality === 'image';
  return {
    chunk_id: str(result.key) || str(meta.chunkId),
    text: isImage
      ? [str(meta.heading_breadcrumb), str(meta.context_text)].filter(Boolean).join(' — ')
      : str(meta.source),
    title: str(meta.title),
    url: str(meta.source_url),
    published_date: str(meta.published_date).slice(0, 10),
    modality,
    image_url: isImage ? str(meta.source) : '',
  };
};

const formatIsoDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export const buildNovaRagMetadataFilter = (fields: Record<string, string[]>, months = 12): Record<string, unknown> => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - months, now.getDate());
  return {
    site_id: NOVA_SITE_ID,
    modality: ['text', 'table'],
    ...fields,
    published_date: { $gte: formatIsoDate(start), $lte: `${formatIsoDate(now)}T23:59:59Z` },
  };
};

export const searchNovaRag = async (
  query: string,
  metadataFilter?: Record<string, unknown>,
): Promise<NovaRagChunk[]> => {
  const resp = await fetch(RAG_SEARCH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, ...(metadataFilter ? { metadata_filter: metadataFilter } : {}), top_k: RAG_TOP_K }),
  });

  if (!resp.ok) {
    throw new Error(`Nova search request failed (${resp.status})`);
  }

  const data: { results?: NovaRagSearchResult[] } = await resp.json();
  return (data.results ?? []).map(normaliseRagChunk).filter((chunk) => chunk.text || chunk.image_url);
};

const resolveRagImageUrl = async (s3Uri: string): Promise<string> => {
  if (!s3Uri.startsWith('s3://')) return s3Uri;
  try {
    const resp = await fetch(`${RAG_IMAGE_URL}?s3_uri=${encodeURIComponent(s3Uri)}`);
    if (!resp.ok) return '';
    const data: { url?: string } = await resp.json();
    return data.url ?? '';
  } catch {
    return '';
  }
};

const formatReportChunk = (chunk: NovaRagChunk): string => {
  const header = [
    chunk.title && `Report: ${chunk.title}`,
    chunk.published_date && `Published: ${chunk.published_date}`,
  ].filter(Boolean);
  return header.length ? `${header.join('\n')}\n\n${chunk.text}` : chunk.text;
};

// The report service's firewall scores the whole body, so a particular mix of article
// chunks can get a 403 even though each chunk passes alone. On a 403, retry leaving
// out one chunk at a time and keep the first set that goes through.
export const generateNovaReport = async (params: {
  prompt: string;
  chunks: NovaRagChunk[];
  userProfileId: string;
}): Promise<{ report: string; chunks: NovaRagChunk[] }> => {
  const request = (chunks: NovaRagChunk[]) =>
    fetch(RAG_REPORT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        RawChunks: chunks.map(formatReportChunk),
        Prompt: params.prompt,
        ModelName: RAG_REPORT_MODEL,
        UserProfileId: String(params.userProfileId),
      }),
    });

  let chunks = params.chunks;
  let resp = await request(chunks);

  for (let skip = 0; resp.status === 403 && skip < params.chunks.length && params.chunks.length > 1; skip++) {
    chunks = params.chunks.filter((_, index) => index !== skip);
    resp = await request(chunks);
  }

  if (!resp.ok) {
    throw new Error(`Nova report request failed (${resp.status})`);
  }

  const data: { report?: string } = await resp.json();
  return { report: data.report ?? '', chunks };
};

export const buildNovaReport = async (params: {
  prompt: string;
  searchQuery: string;
  metadataFilter?: Record<string, unknown>;
  userProfileId: string;
}): Promise<{ content: string; citations: NovaAvaCitation[]; images: NovaAvaStreamImage[] }> => {
  const chunks = (await searchNovaRag(params.searchQuery, params.metadataFilter)).filter((chunk) => chunk.text);

  if (chunks.length === 0) {
    return {
      content: "Nova couldn't find any Stylus coverage matching these filters. Try a broader industry or topic.",
      citations: [],
      images: [],
    };
  }

  const [{ report: content, chunks: usedChunks }, allImageUrls] = await Promise.all([
    generateNovaReport({ prompt: params.prompt, chunks, userProfileId: params.userProfileId }),
    Promise.all(chunks.map((chunk) => (chunk.image_url ? resolveRagImageUrl(chunk.image_url) : Promise.resolve('')))),
  ]);
  const imageUrls = usedChunks.map((chunk) => allImageUrls[chunks.indexOf(chunk)]);

  const citations: NovaAvaCitation[] = usedChunks.map((chunk, index) => ({
    citation_number: index + 1,
    title: chunk.title,
    url: chunk.url,
    published_date: chunk.published_date || undefined,
    modality: chunk.modality,
    chunk_id: chunk.chunk_id,
  }));

  const images: NovaAvaStreamImage[] = usedChunks.flatMap((chunk, index) =>
    imageUrls[index]
      ? [{ chunk_id: chunk.chunk_id, url: imageUrls[index], source: chunk.url, citation_number: index + 1 }]
      : [],
  );

  return { content, citations, images };
};

export const isNovaStreamAbort = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

export const streamNovaAvaChat = async (
  payload: NovaAvaChatPayload,
  handlers: NovaAvaStreamHandlers,
  { signal }: { signal?: AbortSignal } = {},
): Promise<void> => {
  const resp = await fetch(AVA_STREAM_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!resp.ok || !resp.body) {
    throw new Error(`Nova stream request failed (${resp.status})`);
  }

  const emit = (chunk: NovaAvaStreamChunk | null) => {
    if (!chunk) return;
    // TEMP: surfaces the exact shape of any chunk type not already handled below
    // (e.g. a dedicated sources/citations event, mirroring how `images` turned out
    // to be its own event rather than a `sources`-shaped field) — remove once the
    // "Reference content" links are confirmed working end-to-end.
    if (!['response', 'context_status'].includes(chunk.type) && !chunk.sources && !chunk.images && !chunk.citations) {
      console.debug('[Nova stream] unrecognised chunk:', chunk);
    }
    if (chunk.type === 'response' && chunk.message) handlers.onToken?.(chunk.message);
    if (chunk.sources) handlers.onSources?.(chunk.sources);
    if (chunk.suggestedQuestions) handlers.onSuggestedQuestions?.(chunk.suggestedQuestions);
    if (chunk.images) {
      const missingTitle = chunk.images.filter((image) => !image.title?.trim());
      console.log(
        `[Nova stream] images: ${chunk.images.length}, with title: ${chunk.images.length - missingTitle.length}`,
      );
      console.table(
        chunk.images.map(({ chunk_id, title, image_caption, document_url }) => ({ chunk_id, title, image_caption, document_url })),
      );
      if (missingTitle.length)
        console.warn(
          '[Nova stream] images missing title:',
          missingTitle.map((image) => image.chunk_id),
        );
      handlers.onImages?.(chunk.images);
    }
    if (chunk.citations) handlers.onCitations?.(chunk.citations);
  };

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    lines.forEach((line) => emit(parseAvaStreamLine(line)));
  }

  emit(parseAvaStreamLine(buffer));
};
